import type { PortalOrder, PortalOrderItem, PortalReturnSummary } from "../../domain/portal";
import { httpJson, httpUpload } from "../http/client";
import { mockPortalOrder } from "./mocks";

const USE_MOCKS = (process.env.REACT_APP_USE_MOCKS || "false") === "true";

export async function portalStart(_portalSlug: string): Promise<{ ok: true }> {
  if (USE_MOCKS) return { ok: true };
  return httpJson<{ ok: true }>(`/portal/start`);
}

export async function requestOtp(
  portalSlug: string,
  orderNumber: string,
  contact: string,
  channel: "email" | "sms" = "email"
) {
  if (USE_MOCKS) return { ok: true, message: "OTP sent (mock)" };
  return httpJson<{ ok: true; message: string; otp_dev?: string }>(`/portal/request-otp`, {
    method: "POST",
    body: JSON.stringify({
      portal_public_slug: portalSlug,
      order_number: orderNumber,
      channel,
      contact,
    }),
  });
}

export async function verifyOtp(
  portalSlug: string,
  orderNumber: string,
  contact: string,
  otp: string,
  channel: "email" | "sms" = "email"
) {
  if (USE_MOCKS) return { ok: true, session_token: "mock-token", expires_at: "" };
  return httpJson<{ ok: true; session_token: string; expires_at: string }>(`/portal/verify-otp`, {
    method: "POST",
    body: JSON.stringify({
      portal_public_slug: portalSlug,
      order_number: orderNumber,
      channel,
      contact,
      otp,
    }),
  });
}

/**
 * Fetch order items from Salla via the backend.
 */
export async function getOrderItems(portalSlug: string): Promise<{
  order: { id: number; reference_id?: string; status?: any; amounts?: any };
  items: PortalOrderItem[];
}> {
  if (USE_MOCKS)
    return {
      order: { id: 0, reference_id: mockPortalOrder.orderNumber },
      items: mockPortalOrder.items.map((it) => ({ ...it, quantity: 1 })),
    };

  const resp = await httpJson<{
    ok: boolean;
    order: any;
    items: any[];
  }>(`/portal/order-items`);

  return {
    order: resp.order,
    items: (resp.items || []).map((it: any) => ({
      id: String(it.id),
      product_id: it.product_id,
      product_name: it.product_name,
      title: it.product_name || `Item #${it.id}`,
      sku: it.sku || "",
      priceSar: it.price?.amount ?? 0,
      price: it.price,
      quantity: it.quantity ?? 1,
      imageUrl: it.thumbnail,
      options: it.options ?? [],
      categories: it.categories ?? [],
    })),
  };
}

/** @deprecated Use getOrderItems instead */
export async function getOrder(portalSlug: string): Promise<PortalOrder> {
  if (USE_MOCKS) return mockPortalOrder;
  const data = await getOrderItems(portalSlug);
  return {
    id: data.order.id,
    orderNumber: data.order.reference_id || String(data.order.id),
    email: "",
    items: data.items,
  };
}

export async function createReturn(
  _portalSlug: string,
  payload: {
    requested_resolution: string;
    notes_customer?: string;
    order_id_external?: string;
    is_warranty?: boolean;
    customer_email?: string;
    items: Array<{
      order_item_id_external?: string;
      sku: string;
      product_name?: string;
      quantity: number;
      unit_price?: number;
      reason_code: string;
      reason_note?: string;
      category_id_external?: string;
    }>;
  }
) {
  if (USE_MOCKS) return { ok: true, return_number: "RMA-99281" };
  return httpJson<{ ok: true; return_number: string; return_request_id: string }>(`/portal/returns`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function listReturns(): Promise<{ returns: PortalReturnSummary[] }> {
  if (USE_MOCKS) return { returns: [] };
  const resp = await httpJson<{ ok: boolean; returns: PortalReturnSummary[] }>(`/portal/returns`);
  return { returns: resp.returns || [] };
}

export async function getReturnDetails(returnNumber: string) {
  if (USE_MOCKS) return null;
  return httpJson<{ ok: boolean; return: any }>(`/portal/returns/${returnNumber}`);
}

export async function cancelReturn(returnNumber: string, reason?: string) {
  return httpJson<{ ok: boolean }>(`/portal/returns/${returnNumber}/cancel`, {
    method: "POST",
    body: JSON.stringify({ reason }),
  });
}

export async function uploadAttachment(returnNumber: string, file: File) {
  const formData = new FormData();
  formData.append("file", file);
  return httpUpload<{ ok: boolean; attachment: any }>(`/portal/returns/${returnNumber}/attachments`, formData);
}
